/**
 * 지역 추가배송비 — 고도몰 배송조건 `addArea` 이관 (order-service V62).
 *
 * 배송지 주소에 지역명이 들어 있으면 그 지역의 추가요금을 더한다. 공백은 무시하고 비교한다. 여러 지역이
 * 함께 맞으면 **가장 구체적인(긴) 지역 1건만** 적용한다 — 합산하지 않는다.
 *
 *   "제주특별자치도 제주시" 3,000 · "제주특별자치도 제주시 추자면 대서3길" 10,000
 *   → 추자면 대서3길 주소는 10,000 만, 그 외 제주시 주소는 3,000.
 *
 * 우편번호는 더 이상 매칭에 쓰지 않는다. 국내 배송비조건(`regionSurcharges[].label`)과 해외 자체요율
 * 국가그룹(`countryGroups[].regionSurcharges[].areaName`)이 같은 규칙을 쓴다. 매칭은 be-order
 * `RegionAddressMatcher` 가 하고, BO 는 입력과 검증만 한다.
 */

/** BE 가 받는 지역명 최대 길이(@Size(max = 200)). */
export const REGION_AREA_NAME_MAX = 200;

/**
 * 편집용 행. 국내(label · surchargeFee)와 해외(areaName · surchargeKrw)의 필드 이름이 달라, 공용 입력
 * 테이블은 이 형태만 다루고 각 화면이 자기 필드로 옮긴다.
 */
export interface RegionSurchargeRow {
  /** 기존 행이면 서버 id. 새 행·CSV 로 들어온 행은 없다. */
  id?: string;
  areaName: string;
  /** 추가요금(원). 0 이상 정수. */
  fee: number;
}

/** 저장을 막는 문제. `index` 는 전체 목록 기준 0부터. */
export type RegionSurchargeProblem =
  | { kind: 'nameRequired'; index: number }
  | { kind: 'nameTooLong'; index: number; name: string }
  | { kind: 'fee'; index: number; name: string };

/** 비교용 — 공백 제거·소문자. BE 매처와 같은 정규화다. */
export const normalizeAreaName = (name: string): string => name.replace(/\s+/g, '').toLowerCase();

/**
 * be-order `RegionSurchargeUpsert`(areaName/label @NotBlank @Size(max=200), 요금 @NotNull @PositiveOrZero Long)
 * 와 같은 기준으로 검사한다. 걸리는 첫 행을 돌려준다.
 */
export const validateRegionSurcharges = (
  rows: ReadonlyArray<RegionSurchargeRow>,
): RegionSurchargeProblem | null => {
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (!row) continue;
    const name = row.areaName.trim();
    if (name === '') return { kind: 'nameRequired', index };
    if (name.length > REGION_AREA_NAME_MAX) return { kind: 'nameTooLong', index, name };
    if (!Number.isInteger(row.fee) || row.fee < 0) return { kind: 'fee', index, name };
  }
  return null;
};

/**
 * 같은 지역명(공백·대소문자 무시)이 두 번 이상 나오는 이름들. 저장은 막지 않는다 — BE 도 막지 않고,
 * 이관 데이터에 있을 수 있다. 다만 어느 요금이 붙을지 알 수 없으니 화면에서 알린다.
 */
export const duplicateAreaNames = (rows: ReadonlyArray<RegionSurchargeRow>): string[] => {
  const seen = new Map<string, string>();
  const dup: string[] = [];
  for (const row of rows) {
    const key = normalizeAreaName(row.areaName);
    if (key === '') continue;
    const first = seen.get(key);
    if (first === undefined) seen.set(key, row.areaName.trim());
    else if (!dup.includes(first)) dup.push(first);
  }
  return dup;
};

/**
 * 지역명 가나다순 정렬(새 배열). 불러올 때 쓴다 — 목록 순서가 저장할 때마다 바뀌지 않게.
 *
 * 국내 배송비조건은 BE 가 우편번호 앞자리 순으로 돌려주는데(findByPolicyIdOrderByPostalPrefixAsc), V62 부터
 * 우편번호를 보내지 않아 저장 후 전 행이 NULL 이 되고, 행 id 도 저장마다 새로 만들어져 순서가 매번 뒤섞였다.
 * 해외 국가그룹은 BE 가 이미 지역명순이다(findByGroupIdOrderByAreaNameAsc) — 같은 순서로 맞춘다.
 * 순서는 요금에 영향이 없다(가장 구체적인 1건 규칙).
 */
export const sortByAreaName = <T>(rows: ReadonlyArray<T>, name: (row: T) => string): T[] =>
  [...rows].sort((a, b) => name(a).localeCompare(name(b), 'ko'));

/** 저장 직전 정리 — 지역명 앞뒤 공백 제거. */
export const trimRegionSurcharges = (rows: ReadonlyArray<RegionSurchargeRow>): RegionSurchargeRow[] =>
  rows.map((r) => ({ ...r, areaName: r.areaName.trim() }));

export const REGION_SURCHARGE_CSV_HEADER = '지역명,추가배송비';

/** 양식 CSV(헤더 + 예시 2행). BOM 으로 Excel 한글 깨짐을 막는다. */
export const buildRegionSurchargeTemplateCsv = (): string =>
  `﻿${REGION_SURCHARGE_CSV_HEADER}\n제주특별자치도,3000\n경상북도 울릉군 울릉읍 울릉순환로,5000\n`;

/**
 * CSV 텍스트 → 행. `지역명,추가배송비` 2열이 기본이고, 예전 양식 `지역명,우편번호,추가배송비` 3열도
 * 받는다 — 첫 열이 지역명, 마지막 열이 요금이고 가운데 열은 무시한다. 요금이 숫자가 아닌 줄(헤더)과
 * 지역명이 빈 줄은 건너뛴다. 따옴표 안의 쉼표 같은 복잡한 경우는 지원하지 않는다(단순 쉼표 분리).
 */
export const parseRegionSurchargeCsv = (text: string): RegionSurchargeRow[] => {
  const rows: RegionSurchargeRow[] = [];
  for (const rawLine of text.replace(/^﻿/, '').split(/\r?\n/)) {
    const cols = rawLine.split(',').map((c) => c.trim());
    if (cols.length < 2) continue;
    const areaName = cols[0] ?? '';
    const feeRaw = cols[cols.length - 1] ?? '';
    if (areaName === '' || feeRaw === '' || !Number.isFinite(Number(feeRaw))) continue;
    rows.push({ areaName, fee: Math.max(0, Math.trunc(Number(feeRaw))) });
  }
  return rows;
};
